import { useCallback, useState } from 'react';

type Layout = 'grid' | 'list';

/**
 * Hook for managing a layout preference ('grid' or 'list') persisted to localStorage.
 *
 * @param storageKey - The localStorage key used to persist the layout selection.
 * @param defaultLayout - The fallback layout when no valid value is stored. Defaults to `'grid'`.
 * @returns A tuple of the current layout and a setter function to update it.
 */
function useLayoutSelector(
  storageKey: string,
  defaultLayout: Layout = 'grid'
): [Layout, (layout: Layout) => void] {
  const [layout, setLayout] = useState<Layout>(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      return stored === 'grid' || stored === 'list' ? stored : defaultLayout;
    } catch {
      return defaultLayout;
    }
  });

  const handleSetLayout = useCallback(
    (value: Layout) => {
      setLayout(value);
      try {
        window.localStorage.setItem(storageKey, value);
        // eslint-disable-next-line no-empty
      } catch {}
    },
    [storageKey]
  );

  return [layout, handleSetLayout] as const;
}

export { useLayoutSelector };
export type { Layout };
